import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import { FaUsers, FaDollarSign, FaChartBar, FaBullseye, FaChartLine } from 'react-icons/fa';
import 'swiper/css';
import 'swiper/css/navigation';

const terms = [
  { label: 'Members', maxValue: 10000, icon: <FaUsers className="text-lg" />, isPercent: false, isMoney: false },
  { label: 'Total Profit', maxValue: 500000, icon: <FaDollarSign className="text-lg" />, isPercent: false, isMoney: true },
  { label: 'Daily Trades', maxValue: 200, icon: <FaChartBar className="text-lg" />, isPercent: false, isMoney: false },
  { label: 'Success Rate', maxValue: 95, icon: <FaBullseye className="text-lg" />, isPercent: true, isMoney: false },
  { label: 'Volume Traded', maxValue: 1000000, icon: <FaChartLine className="text-lg" />, isPercent: false, isMoney: true },
];

const formatNumber = (num, term) => {
  if (term.isPercent) return num + '%';
  if (term.isMoney) {
    if (num >= 1000000) return '$' + (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return '$' + (num / 1000).toFixed(1) + 'K';
    return '$' + num;
  }
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K+';
  return num + '+';
};

export default function LineCounter() {
  const [counts, setCounts] = useState(terms.map(() => 0));
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !hasStarted) setHasStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;
    const intervals = [];
    terms.forEach((term, index) => {
      let current = 0;
      const step = Math.ceil(term.maxValue / 60);
      intervals[index] = setInterval(() => {
        current = Math.min(current + step, term.maxValue);
        setCounts((prev) => { const u = [...prev]; u[index] = current; return u; });
        if (current >= term.maxValue) clearInterval(intervals[index]);
      }, 30);
    });
    return () => intervals.forEach(clearInterval);
  }, [hasStarted]);

  return (
    <div ref={ref} className="relative w-full py-8 overflow-hidden" style={{ background: "linear-gradient(135deg, #0a1628, #050a14)" }}>
      <div className="absolute top-0 left-0 w-full h-px"
        style={{ background: "linear-gradient(90deg, transparent, #22c55e 30%, #3b82f6 70%, transparent)" }} />
      <div className="absolute bottom-0 left-0 w-full h-px"
        style={{ background: "linear-gradient(90deg, transparent, #3b82f6 30%, #22c55e 70%, transparent)" }} />
      <motion.div className="absolute top-0 left-0 w-full h-0.5 pointer-events-none"
        style={{ background: "linear-gradient(90deg, transparent, rgba(34,197,94,0.6), transparent)" }}
        animate={{ x: ["-100%", "100%"] }} transition={{ duration: 3, repeat: Infinity, ease: "linear" }} />

      <div className="max-w-7xl mx-auto px-4">
        <Swiper modules={[Navigation, Autoplay]} spaceBetween={20} slidesPerView={2}
          autoplay={{ delay: 3000, disableOnInteraction: false }} grabCursor={true}
          breakpoints={{ 640: { slidesPerView: 3 }, 768: { slidesPerView: 4 }, 1024: { slidesPerView: 5 } }}
          className="w-full">
          {terms.map((term, index) => (
            <SwiperSlide key={index}>
              <motion.div className="flex flex-col items-center text-center py-2"
                initial={{ opacity: 0, y: 20 }} animate={hasStarted ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.1 }} whileHover={{ scale: 1.08 }}>
                <div className="mb-2" style={{ color: index % 2 === 0 ? "#22c55e" : "#3b82f6" }}>
                  {term.icon}
                </div>
                <motion.span className="font-orbitron text-2xl sm:text-3xl font-black"
                  style={{ background: index % 2 === 0 ? "linear-gradient(135deg, #22c55e, #86efac)" : "linear-gradient(135deg, #3b82f6, #93c5fd)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  {formatNumber(counts[index], term)}
                </motion.span>
                <span className="text-xs text-slate-400 uppercase tracking-widest mt-1 font-mono-code">
                  {term.label}
                </span>
              </motion.div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}
